import classNames from 'classnames'
import { useFormikContext } from 'formik'
import * as React from 'react'
import { useTranslatable } from 'src/hooks/locale/utils'
import { SEMESTERS } from './constants'
import { type CourseEditFormValues } from './types'

export function StudentSemesterGrid(): JSX.Element {
  const formik = useFormikContext<CourseEditFormValues>()
  const t = useTranslatable()
  const value = React.useMemo(() => formik.values.userSem ?? [], [formik.values.userSem])

  const toggleSemester = React.useCallback(
    (semester: number) => {
      const current = formik.values.userSem ?? []
      const next = current.includes(semester)
        ? current.filter((s) => s !== semester)
        : [...current, semester].sort((a, b) => a - b)
      formik.setFieldValue('userSem', next)
    },
    [formik]
  )

  const selectAll = React.useCallback(() => {
    formik.setFieldValue('userSem', [...SEMESTERS])
  }, [formik])

  const deselectAll = React.useCallback(() => {
    formik.setFieldValue('userSem', [])
  }, [formik])

  return (
    <div className="rounded-md border border-borderGrey bg-card p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-primaryTextColor">{t('student:student_semester')}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={selectAll} className="text-xs text-primaryBlueLink hover:underline">
            {t('common:select_all')}
          </button>
          <span className="text-captionColor">|</span>
          <button type="button" onClick={deselectAll} className="text-xs text-primaryBlueLink hover:underline">
            {t('common:deselect_all')}
          </button>
        </div>
      </div>

      <div className="sm:grid-cols-15 mt-4 grid grid-cols-10 gap-2">
        {SEMESTERS.map((semester) => {
          const checked = value.includes(semester)
          const id = `course-semester-${semester}`
          return (
            <label
              key={semester}
              htmlFor={id}
              className={classNames(
                'relative inline-flex cursor-pointer items-center justify-center rounded border p-1.5 transition-colors',
                checked
                  ? 'border-primaryBlue bg-primaryBlue/10 text-primaryBlue'
                  : 'border-borderGrey bg-card hover:border-primaryBlue/50 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={() => toggleSemester(semester)}
                className="peer sr-only"
              />
              <span className="text-xs font-medium text-primaryTextColor">{semester}</span>
            </label>
          )
        })}
      </div>
    </div>
  )
}
